<template>
    <div class="timeline">
        <div class="timeline-box">
            <div class="timeBox">
                <journey-details v-for="(item, index) in timeline" v-bind:key="'journey-' + index" :journey="item" :index="index" @active-journey="activeJourney(item)"></journey-details>

                <div v-for="(item, index) in time" v-bind:key="'time-' + index" :style="{left: index * percentPerTime + '%'}" class="time">
                    <div class="label">
                        <div class="line"></div>
                        {{ item.label }}
                    </div>
                </div>
                <v-tooltip v-for="(event, index) in journey.events" :key="event.id" top>
                    <template v-slot:activator="{ on }">
                        <div v-bind:key="'event-' + index" class="event" :class="{hidden: !event.isVisible }" :style="{left: calculateEventOffset(event) + '%'}" v-on="on" @click="activeEvent(event)"></div>
                    </template>
                    <span>{{ event.vendorEventType + ' ' + calculateFormat(event.triggerOn) }}</span>
                </v-tooltip>
            </div>
        </div>
    </div>
</template>

<script>
import JourneyDetails from '@/views/Journeys/Timeline/JourneyDetails'
import moment from 'moment'
import { searchEnumById } from '@/helpers/Utils'
import { mapGetters } from 'vuex'

export default {
    name: 'Timeline',
    components: { JourneyDetails },
    data () {
        return {
            time: [],
            localEvents: [],
            percentPerTime: 0,
            timeline: []
        }
    },
    props: {
        journey: {
            required: true
        },
        events: {
            required: true
        },
        eventsToShow: {
            required: true
        }
    },
    mounted () {
        this.calculateTimes()
        this.drawTimeline()
    },
    beforeUpdate () {
        this.$forceUpdate()
    },
    updated () {
        this.localEvents = this.events
    },
    watch: {
        journey: {
            handler: function () {
                this.calculateTimes()
                this.drawTimeline()
            },
            deep: true
        }
    },
    computed: {
        ...mapGetters({
            timelineBlockStatuses: 'dictionaryModule/getTimelineBlockStatuses'
        })
    },
    methods: {
        activeJourney (item) {
            this.$emit('active-journey', item)
        },
        activeEvent (event) {
            this.$emit('active-event-popup', { eventId: event.id, hardwareId: event.hardwareId })
            this.$emit('active-event', event)
        },
        calculateTimes () {
            if (null == this.journey) {
                return
            }

            this.time = []
            let minutesToAdd = 30
            const start = moment(this.getPointTimestamp(this.journey.startPoint))
            const end = moment(this.getPointTimestamp(this.journey.endPoint))

            const pieces = end.diff(start, 'seconds')

            if (12 < end.diff(start, 'hours')) {
                minutesToAdd = 60
            }

            while (start.isBefore(end)) {
                this.time.push({
                    'label': start.format('HH:mm'),
                    'position': 0
                })

                start.add(minutesToAdd, 'minutes')
            }

            let piecesPerTime = pieces / this.time.length
            this.percentPerTime = (piecesPerTime * 100) / pieces
        },
        drawTimeline () {
            if (null == this.journey || null == this.journey.timelineBlocks) {
                return
            }

            this.timeline = []
            const start = moment(this.getPointTimestamp(this.journey.startPoint))
            const end = moment(this.getPointTimestamp(this.journey.endPoint))
            const pieces = end.diff(start, 'seconds')

            let total = 0
            this.journey.timelineBlocks.filter((item) => {
                const start = moment(item.startTime)
                return start.isBefore(end)
            }).forEach((timeBlock) => {
                const timeBlockStart = moment(timeBlock.startTime)
                const timeBlockEnd = moment(timeBlock.endTime)
                let sec = timeBlockEnd.diff(timeBlockStart, 'seconds')
                let percent = (sec * 100) / pieces
                percent = Math.round(percent * 100) / 100
                const timelineStatus = searchEnumById(this.timelineBlockStatuses, timeBlock.status)

                const duration = moment.utc(timeBlockEnd.diff(timeBlockStart)).format('HH:mm:ss')
                total += percent

                this.timeline.push({
                    percent: percent,
                    status: timeBlock.status,
                    statusName: timelineStatus.toLowerCase(),
                    duration: duration,
                    startAddress: timeBlock.startAddress ? timeBlock.startAddress.label : 'Unknown',
                    endAddress: timeBlock.endAddress ? timeBlock.endAddress.label : 'Unknown',
                    startTime: timeBlockStart.format('HH:mm:ss'),
                    endTime: timeBlockEnd.format('HH:mm:ss'),
                    total: total
                })
            })
        },
        calculateEventOffset (event) {
            const start = moment(this.getPointTimestamp(this.journey.startPoint))
            const end = moment(this.getPointTimestamp(this.journey.endPoint))
            const eventTime = moment(event.triggerOn)

            const pieces = end.diff(start, 'seconds')

            let sec = eventTime.diff(start, 'seconds')
            let percent = (sec * 100) / pieces
            percent = Math.round(percent * 100) / 100
            return percent
        },
        getPointTimestamp (point) {
            return point.utcTimestamp ? point.utcTimestamp : point.utc
        },
        calculateFormat (time) {
            const eventTime = moment(time)
            return eventTime.format('HH:mm')
        }
    }
}
</script>

<style scoped lang="scss">
.timeline {
    display: flex;
    margin-top: 30px;
    margin-bottom: 50px;

    .timeline-box {
        display: flex;
        width: 100%;
        position: relative;
        top: 14px;

        .timeBox {
            display: flex;
            width: 100%;
            height: 25px;
            background: transparent;
            margin: 0 2px;
            cursor: pointer;
            position: relative;
            z-index: 2;

            .time {
                position: absolute;
                font-size: 12px;
                font-weight: 500;
                bottom: -25px;
                z-index: 1;

                .label {
                    position: relative;
                    left: -14px;
                }

                .line {
                    border-left: 1px dashed rgba(black, 0.4);
                    height: 39px;
                    position: relative;
                    left: 14px;
                    z-index: -1;
                }

                &.before {
                    left: -13px;
                }

                &.after {
                    right: -13px;
                }
            }

            .event {
                position: absolute;
                display: block;
                left: 8%;
                width: 20px;
                height: 41px;
                top: -25px;
                transform: translateX(-50%);
                background-size: contain;
                background-image: url('../../../assets/icons/icon-car-offline.svg');
                cursor: pointer;

                &:hover {
                    opacity: 0.7;
                }

                &.hidden {
                    opacity: 0 !important;
                }
            }
        }
    }
}
</style>
